import { Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import moment from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, startWith, tap } from 'rxjs/operators';
import { MaterialPickupModel } from '../../components/material-pickup/material-pickup.model';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { ArrayModel } from '../../models/complex/array.model';
import { CseDopModel } from '../../models/complex/cse-dop.model';
import { MaterialCentralReturnModel } from '../../models/complex/material-central-return.model';
import { LocalStorageNotificationMaterialOrderModel } from '../../models/low-level/local-storage-notification-material-order.model';
import { MaterialIdentifierModel } from '../../models/low-level/material-identifier.model';
import { MATERIAL_SOURCE_CODE } from '../../models/low-level/material-source.model';
import { MATERIAL_STATUS_CODE } from '../../models/low-level/material-status.model';
import { JsonMaterialModel, MaterialModel, MaterialModelFromJson } from '../../models/low-level/material.model';
import { CONSUME_CODE, RETURN_CODE } from '../../models/low-level/material.enum';
import {
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '../../models/low-level/notification-identifier.model';
import { NumberModelOptions } from '../../models/options/number-model-options';
import { NumberModel } from '../../models/primitive/number.model';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NotificationDataInstanceToken, NotificationDataInterface } from '../../interfaces/notification-data.interface';
import { EventService } from '../event/event.service';
import { notificationsUpdated$ } from '../../state/notification/notification-updates';
import { MaterialUtilitiesService } from '../material-utilities/material-utilities.service';
import { OdataMaterialService } from './odata-material.service';
import { StorageMaterialService } from './storage-material.service';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';

@Injectable({
    providedIn: 'root',
})
export class MaterialService implements InitializableInterface {
    private materialCseDopList: Array<DisplayDecorator<CseDopModel>> = [];
    private orderedMaterials: MaterialModel[] = [];
    private materials: MaterialModel[] = [];
    private requestedMaterials: LocalStorageNotificationMaterialOrderModel[] = [];
    // Temporary fix until the material service is being migrated to ngrx.
    private requestedMaterialsSubject: BehaviorSubject<LocalStorageNotificationMaterialOrderModel[]> =
        new BehaviorSubject([]);
    public requestedMaterials$ = this.requestedMaterialsSubject.asObservable();

    public isInitialized = false;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly eventService: EventService,
        private readonly odataMaterialService: OdataMaterialService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly storageMaterialService: StorageMaterialService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly materialUtilities: MaterialUtilitiesService,
    ) {
        // get value helper dropdown values on offline store sync receive completed
        notificationsUpdated$()
            .pipe(
                concatMap((isCancellationConfirmed) => {
                    if (isCancellationConfirmed) {
                        return of([]);
                    }
                    this.log.debug(
                        `MaterialService: Calling material service methods after notifications were updated`,
                    );
                    return this.updateLocalStorageMaterialsWithBackendDataAndSave();
                }),
            )
            .subscribe();

        this.eventService.synchronizationRequested$
            .pipe(
                mergeMap(() => this.updateLocalStorageMaterialsWithBackendDataAndSave()),
                mergeMap(() => this.updateLocalRequestedMaterials()),
                tap(() => this.eventService.triggerRefreshMaterialReturnCount()),
            )
            .subscribe();
    }

    /**
     * Initialize at the initialization-service.ts
     * prepare all the required dropdown using value helper
     */
    public init(): Observable<MaterialModel[]> {
        this.log.debug('MaterialService: init started');

        return this.updateLocalStorageMaterialsWithBackendDataAndSave().pipe(
            mergeMap(() => this.updateLocalRequestedMaterials()),
            mergeMap(() => this.odataMaterialService.getCseDop()),
            tap((dopList) => this.setFullDopList(dopList)),
            map(() => {
                this.log.debug('MaterialService: Finished initialization');
                this.isInitialized = true;
                return this.materials;
            }),
        );
    }

    private setOrderedMaterials(): void {
        // create material list
        this.orderedMaterials = [];
        this.notificationService.getAllNotifications().forEach((notification) => {
            notification.partSet.forEach((part) => {
                // create a new model if the material was not saved before
                this.orderedMaterials.push(new MaterialModel(part, notification));
            });
        });
    }

    public updateLocalStorageMaterialsWithBackendDataAndSave(): Observable<void> {
        this.log.debug(`${this.constructor.name}: updateLocalStorageMaterialsWithBackendDataAndSave`);
        this.setOrderedMaterials();
        return this.getAllMaterialFromStore().pipe(
            mergeMap((materials) => {
                // update materials with changes from backend
                materials = this.updateLocalMaterialFromOrdered(materials, this.orderedMaterials);

                // remove materials which are older than materialHistoryLimit days
                materials = this.removeOldMaterials(materials);

                this.materials = materials;

                this.log.debug(
                    `${this.constructor.name}: updateLocalStorageMaterialsWithBackendDataAndSave: update materials`,
                    {
                        numMaterials: materials.length,
                    },
                );

                // store local material list
                return this.saveMaterials(materials);
            }),
            tap(() => this.eventService.triggerMaterialsUpdated()),
        );
    }

    private updateLocalRequestedMaterials(): Observable<void> {
        return this.storageMaterialService.getAllMaterialCreateOrders().pipe(
            mergeMap((localRequestedMaterials) => {
                const requestedMaterials: LocalStorageNotificationMaterialOrderModel[] =
                    localRequestedMaterials.material;

                // remove materials which are older than 14 days (depends on config value)
                this.requestedMaterials = this.removeOldRequestedNotificationMaterials(requestedMaterials);
                // store local material requested list
                this.requestedMaterialsSubject.next(this.requestedMaterials);
                return this.storageMaterialService.saveMaterialCreateOrders(this.requestedMaterials, Date.now());
            }),
        );
    }

    public getMaterials(): MaterialModel[] {
        if (!this.isInitialized) {
            throw new Error(`MaterialService: getMaterials: Trying to call MaterialService before initialization!`);
        }

        return this.materials;
    }

    /**
     * Returns the storage locations that match the frontid of the current user.
     * In P58, this is the entire list we get from the backend.
     * In NGMAA, these are two entries: One for new parts, one for defect parts.
     */
    public getCSEDopList(): Array<DisplayDecorator<CseDopModel>> {
        if (!this.isInitialized) {
            throw new Error(`MaterialService: getCSEDopList: Trying to call MaterialService before initialization!`);
        }

        const frontId = this.configService.getUserFrontId();
        return this.materialCseDopList.filter((dop) => dop.model.frontId === frontId);
    }

    public getFullDopList(): Array<DisplayDecorator<CseDopModel>> {
        if (!this.isInitialized) {
            throw new Error(`MaterialService: getFullDopList: Trying to call MaterialService before initialization!`);
        }

        return this.materialCseDopList;
    }

    private setFullDopList(dopList: CseDopModel[]): void {
        this.materialCseDopList = dopList.map((dop) => {
            // SHCNGMP58-951: dop list with dop number, name1 and city needs to be displayed.
            const label = `${dop.dropOffPoint.value}; ${dop.name1.value}; ${dop.city.value}`;
            return new DisplayDecorator<CseDopModel>(label, dop);
        });
    }

    /**
     * Called from material history component init
     * get all the ordered and requested material from file storage
     * ordered materials are the one which are assigned to a notification
     * requested materials are the one which CSE has requested using “Material Request”
     */
    public getAllMaterialsOrderedAndRequested(): Observable<MaterialModel[]> {
        return this.eventService.refreshMaterialReturnCount$.pipe(
            startWith(undefined),
            map(() => {
                const materialModelFromRequested = this.requestedMaterials.map(
                    (requestMaterial) => requestMaterial.materials,
                );
                return this.getAllOrderedMaterialsWithStatus().concat(...materialModelFromRequested);
            }),
        );
    }

    /**
     * get all the ordered materials for a notification
     * Use Case: Material Pickup
     *
     * @param notificationIdentifier
     */
    public getAllOrderedMaterialsForNotification(notificationIdentifier: NotificationIdentifierModel): MaterialModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getAllOrderedMaterialsForNotification: Trying to call MaterialService before initialization!`,
            );
        }

        // filter all ordered rows for a notification
        // if local storage available load data from it
        return this.materials.filter(
            (material) =>
                // make sure to only return ordered materials
                NotificationIdentifierModel.compare(material.notificationIdentifier, notificationIdentifier) &&
                material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED &&
                material.showOnNotificationDetail &&
                material.isActive,
        );
    }

    /**
     * get all the orders which have a status string set
     * Use case: Material History
     */
    public getAllOrderedMaterialsWithStatus(): MaterialModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getAllOrderedMaterialsWithStatus: Trying to call MaterialService before initialization!`,
            );
        }

        return this.materials.filter(
            (material) =>
                // make sure to only return ordered and split materials and have already a status set
                // must have a status string
                // must have an activity date
                (material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED ||
                    material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK ||
                    material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.OTHERSTOCK ||
                    material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.OTHER ||
                    material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.SPLIT) &&
                material.statusString.value &&
                material.activityDate.value.isValid(),
        );
    }

    public getAllMaterialsForNotification(notificationIdentifier: NotificationIdentifierModel): MaterialModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getAllMaterialsForNotification: Trying to call MaterialService before initialization!`,
            );
        }

        return this.materials.filter(
            (material) =>
                NotificationIdentifierModel.compare(material.notificationIdentifier, notificationIdentifier) &&
                material.isActive,
        );
    }

    public getAllPickedMaterialsForNotification(notificationIdentifier: NotificationIdentifierModel): MaterialModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getAllPickedMaterialsForNotification: Trying to call MaterialService before initialization!`,
            );
        }

        return this.getAllMaterialsForNotification(notificationIdentifier).filter(
            (material) => material.materialStatus.statusCode === MATERIAL_STATUS_CODE.PICKED,
        );
    }

    public getAllBootStockMaterialsForNotification(
        notificationIdentifier: NotificationIdentifierModel,
    ): MaterialModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getAllBootStockMaterialsForNotification: Trying to call MaterialService before initialization!`,
            );
        }

        return this.materials.filter(
            (material) =>
                NotificationIdentifierModel.compare(material.notificationIdentifier, notificationIdentifier) &&
                material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK,
        );
    }

    /*
     * Business logic:
     * Boot stock quantity should be reduced only while the corresponding notification is still available in the dashboard.
     *
     * Once the notification is submitted, the backend becomes the single source of truth for boot stock quantities.
     * Materials can also be returned from the side menu without an associated notification,
     * so RETURNED materials are excluded from the local boot stock calculation.*/
    public getPickedMatStockFromExistingNotification(): MaterialModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getPickedMatStockFromExistingNotification: Trying to call MaterialService before initialization!`,
            );
        }
        return this.materials.filter((material) => {
            if (
                material.materialSource.sourceCode !== MATERIAL_SOURCE_CODE.BOOTSTOCK ||
                material.materialStatus.statusCode === MATERIAL_STATUS_CODE.RETURNED
            ) {
                return false;
            }
            // If the notification is no longer on-device, the backend is the source of truth — exclude locally.
            const notificationState = this.notificationService.getNotificationStateByIdentifier(
                material.notificationIdentifier,
            );
            if (typeof notificationState === 'undefined') {
                return false;
            }
            return (
                this.notificationService.getStatusStateByIdentifier(material.notificationIdentifier).statusCode !==
                NOTIFICATION_STATUS_CODE.CRM
            );
        });
    }

    public getAllPickedBootStockMaterials(): MaterialModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getAllPickedBootStockMaterials: Trying to call MaterialService before initialization!`,
            );
        }
        return this.materials.filter(
            (material) => material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK,
        );
    }

    public pickupMaterial(materialPickup: MaterialPickupModel): Observable<unknown> {
        if (!this.isInitialized) {
            throw new Error(`MaterialService: pickupMaterial: Trying to call MaterialService before initialization!`);
        }

        return this.odataMaterialService.saveMaterialPickup(materialPickup);
    }

    public getAllReturnsFromLocalStorage(): MaterialCentralReturnModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getAllReturnsFromLocalStorage: Trying to call MaterialService before initialization!`,
            );
        }

        const allMaterialReturns = this.materials.filter((materialModel) => materialModel.materialStatus.isReturn);
        return allMaterialReturns.map(
            (materialModel) =>
                new MaterialCentralReturnModel(
                    materialModel,
                    materialModel.notificationIdentifier,
                    this.getCSEDopList(),
                    this.materialUtilities,
                ),
        );
    }

    public static pickedUpQuantityFromLocalStorage(material: MaterialModel): number | undefined {
        if (material.materialStatus.statusCode === MATERIAL_STATUS_CODE.PICKED || material.isConsumed) {
            return material.pickedQuantity.value;
        }
        return 0;
    }

    /**
     * update the local stored material from the ordered material
     * use case: new materials information added or quantity update for already assigned material for a notification
     *
     * @param materials
     * @param orderedMaterials
     */
    private updateLocalMaterialFromOrdered(
        materials: MaterialModel[],
        orderedMaterials: MaterialModel[],
    ): MaterialModel[] {
        // First identify existing materials that were updated from the backend and update those
        orderedMaterials.forEach((orderedMaterial) => {
            // find the matching local stored material using material identifier and source
            const materialToUpdate = materials.find(
                (material) =>
                    MaterialIdentifierModel.compare(material.materialIdentifier, orderedMaterial.materialIdentifier) &&
                    NotificationIdentifierModel.compare(
                        material.notificationIdentifier,
                        orderedMaterial.notificationIdentifier,
                    ) &&
                    material.materialSource.sourceCode === orderedMaterial.materialSource.sourceCode,
            );
            // if found update the material values
            if (materialToUpdate) {
                // update the quantity and max quantity based on new delivered quantity, only if there is a difference
                if (materialToUpdate.deliveredQuantity > orderedMaterial.deliveredQuantity) {
                    materialToUpdate.quantity.value = orderedMaterial.deliveredQuantity;
                } else if (materialToUpdate.deliveredQuantity < orderedMaterial.deliveredQuantity) {
                    materialToUpdate.quantity.value =
                        (materialToUpdate.quantity.value ?? 0) +
                        (orderedMaterial.deliveredQuantity - materialToUpdate.deliveredQuantity);
                }
                if (materialToUpdate.deliveredQuantity !== orderedMaterial.deliveredQuantity) {
                    materialToUpdate.maxQuantity = materialToUpdate.quantity.value ?? 0;
                    materialToUpdate.quantity = assignModelOptions(
                        materialToUpdate.quantity,
                        {
                            ...materialToUpdate.quantity.options,
                            maxValue: materialToUpdate.maxQuantity,
                        },
                        materialToUpdate,
                    );
                }

                // update the remaining values
                materialToUpdate.updateMaterialInfo(orderedMaterial);
            }
        });

        // Second, remove existing materials that were removed by the backend.
        // Use case: Same materials assigned to 2 CSEs. One picks the material, the other CSEs NDP will be refreshed with that material removed from partSet.
        //           So to do that we set material's isActive property to false.
        // This should be done only for ordered materials and not for boot/other stock and manually added material.
        materials.forEach((material) => {
            if (
                !orderedMaterials.some(
                    (orderedMaterial) =>
                        MaterialIdentifierModel.compare(
                            material.materialIdentifier,
                            orderedMaterial.materialIdentifier,
                        ) &&
                        NotificationIdentifierModel.compare(
                            orderedMaterial.notificationIdentifier,
                            material.notificationIdentifier,
                        ),
                )
            ) {
                if (
                    material.materialSource.sourceCode !== MATERIAL_SOURCE_CODE.BOOTSTOCK &&
                    material.materialSource.sourceCode !== MATERIAL_SOURCE_CODE.OTHERSTOCK &&
                    material.materialSource.sourceCode !== MATERIAL_SOURCE_CODE.OTHER
                ) {
                    material.isActive = false;
                }
            }
        });

        // Third, add new materials
        this.orderedMaterials.forEach((orderedMaterial) => {
            if (
                !materials.find(
                    (material) =>
                        MaterialIdentifierModel.compare(
                            material.materialIdentifier,
                            orderedMaterial.materialIdentifier,
                        ) &&
                        NotificationIdentifierModel.compare(
                            orderedMaterial.notificationIdentifier,
                            material.notificationIdentifier,
                        ),
                )
            ) {
                materials.push(orderedMaterial);
            }
        });

        return materials;
    }

    public updateMaterials(materials: MaterialModel[]): Observable<void> {
        materials.forEach((material) => {
            this.updateMaterialInMemory(material);
        });
        return this.saveMaterials(this.materials);
    }

    /**
     * Update the material value that is part of material list and save the updated list. The automated saving is part
     * of the function to make sure that it is not forgotten when calling the service from the outside.
     *
     * @param material
     */
    public updateMaterial(material: MaterialModel): Observable<void> {
        this.updateMaterialInMemory(material);

        return this.saveMaterials(this.materials);
    }

    /**
     * Update the material value that is part of material list *IN MEMORY*
     * This functions should be used for bulk-updates of multiple materials, eg. when splitting material. The saving is
     * not part of the function to avoid the overhead of saving the same information multiple times.
     *
     * You need to trigger saving the material once you are done!
     *
     * @param material
     */
    private updateMaterialInMemory(material: MaterialModel): void {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: updateMaterialInMemory: Trying to call MaterialService before initialization!`,
            );
        }

        this.materials.forEach((materialRow, index) => {
            if (
                MaterialIdentifierModel.compare(materialRow.materialIdentifier, material.materialIdentifier) &&
                materialRow.materialKey === material.materialKey
            ) {
                this.materials[index] = material;
            }
        });
    }

    /**
     * Save the passed in materials to local storage
     *
     * @param material
     */
    public addMaterialToLocalStorage(material: MaterialModel): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: addMaterialToLocalStorage: Trying to call MaterialService before initialization!`,
            );
        }

        this.materials.push(material);
        // finally save the data back to local storage
        return this.saveMaterials(this.materials);
    }

    /**
     * Save the passed in materials to local storage
     *
     * @param materials
     */
    public addMaterialsToLocalStorage(materials: MaterialModel[]): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: addMaterialsToLocalStorage: Trying to call MaterialService before initialization!`,
            );
        }

        this.materials.push(...materials);
        // finally save the data back to local storage
        return this.saveMaterials(this.materials);
    }

    /**
     * get all locally stored materials
     */
    private getAllMaterialFromStore(): Observable<MaterialModel[]> {
        return this.storageMaterialService
            .getMaterials()
            .pipe(map((localJsonMaterials) => this.materialsFromJson(localJsonMaterials.material)));
    }

    /**
     * Rebuilds materials from their JSON form, the way they are read back from local storage.
     *
     * @param jsonMaterials the materials as `MaterialModel.toJSON()` wrote them
     */
    public materialsFromJson(jsonMaterials: readonly JsonMaterialModel[]): MaterialModel[] {
        return jsonMaterials.map((jsonMaterial) => {
            const notification = this.notificationService.getNotificationByIdentifier(
                new NotificationIdentifierModelFromJson(jsonMaterial.notificationIdentifier),
            );
            return new MaterialModelFromJson(jsonMaterial, notification);
        });
    }

    // End Local Storage

    /**
     * Set status based on "Not Received" or "Picked"
     *
     * @param materials
     * @param index
     * @param quantity
     * @param statusCode
     */
    public setStatus(
        materials: ArrayModel<MaterialModel>,
        index: number,
        quantity: number,
        statusCode: string,
    ): Observable<unknown> {
        if (!this.isInitialized) {
            throw new Error(`MaterialService: setStatus: Trying to call MaterialService before initialization!`);
        }

        const materialModel = materials.value[index];
        materialModel.activityDate.value = moment();

        materialModel.pickedQuantity.value = quantity;
        // split the rows if the pickup quantity does not match with ordered quantity
        // example ordered quantity is 6 and user picked 4 then the rows has to be split
        if (materialModel.pickedQuantity.value !== materialModel.quantity.value) {
            // set the new quantity value before split
            // we need to change the reference in order for the change detection to be detected on the existing materials.
            materialModel.quantity = <NumberModel>(
                materialModel.set(
                    new NumberModel(
                        materialModel.quantity.name,
                        materialModel.pickedQuantity.value,
                        materialModel.quantity.options,
                    ),
                )
            );

            this.splitMaterial(materials, index, materialModel.pickedQuantity.value, true).pipe(
                catchError((error) => {
                    this.log.error(
                        'MaterialService: Error in MaterialService -> setStatus -> splitMaterial: ',
                        JSON.stringify(error),
                    );
                    return of(undefined);
                }),
            );
        } else if (materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.SPLIT) {
            if (materialModel.parentMaterialModelIndex === -1) {
                throw new Error(
                    `MaterialService: Found material resulting from split that has no parent material model index!`,
                );
            }

            // If material is being picked up from already split row, it should now behave independently from its parent row,
            // ie. further split or merge on this row should now not affect its parent row
            this.removeParentRowDependencyFromChild(
                materialModel,
                materialModel.pickedQuantity.value,
                materials.value[materialModel.parentMaterialModelIndex],
            );
        }
        // material is being picked from parent row, then find all the child rows of this parent and remove their dependency
        MaterialService.getChildRows(materials, index).forEach((child) => {
            this.removeParentRowDependencyFromChild(child, child.quantity.value ?? 0, materialModel);
        });

        // if the complete ordered material quantity is picked up then set the row status directly to status "Picked" or "Not Received"
        switch (statusCode) {
            case MATERIAL_STATUS_CODE.NOTRECEIVED:
                materialModel.quantity = assignModelOptions(
                    materialModel.quantity,
                    {
                        ...materialModel.quantity.options,
                        readonly: true,
                    },
                    materialModel,
                );
                // "X" is code for not received in consume dropdown

                materialModel.consumptionCode.selectByValue(CONSUME_CODE.NOT_RECEIVED);
                materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.NOTRECEIVED;
                materialModel.statusString.value = 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_NOT_RECEIVED';

                break;
            case MATERIAL_STATUS_CODE.PICKED:
                materialModel.quantity = assignModelOptions(
                    materialModel.quantity,
                    {
                        ...materialModel.quantity.options,
                        readonly: false,
                    },
                    materialModel,
                );
                materialModel.consumptionCode.deselect();
                materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.PICKED;
                materialModel.statusString.value = 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_PICKEDUP';

                break;
        }
        // map the Pickup RFID(UCR number) from Pickup Screen to AWB Central Return field
        if (materialModel.ucrNumber && !materialModel.hasSmartSensorTracker()) {
            materialModel.awbReturn.value = materialModel.ucrNumber;
        }
        // update the values in local storage
        this.updateMaterialInMemory(materialModel);
        return this.saveMaterials(this.materials);
    }

    /**
     * Split the row based on different conditions
     *
     * @param materials
     * @param index
     * @param quantity
     * @param splitFromPickup
     */
    public splitMaterial(
        materials: ArrayModel<MaterialModel>,
        index: number,
        quantity: number,
        splitFromPickup = false,
    ): Observable<unknown> {
        if (!this.isInitialized) {
            throw new Error(`MaterialService: splitMaterial: Trying to call MaterialService before initialization!`);
        }

        // get material from index
        const materialModel = materials.value[index];

        // Condition for splitting a row
        // 1. Only parent row can be split
        // 2. The maxQuantity has to be more than current quantity
        // 3. Quantity should be more than 0
        // 4. Only ordered row can be split, not the split, boot stock and other stock rows
        // 5. Row can only be split on decrement of quantity
        if (materialModel.maxQuantity >= quantity && quantity > 0) {
            if (materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED) {
                const childMaterials = MaterialService.getChildRows(materials, index);

                // get the total quantity sum by looping over same status parent child code
                let totalQuantity = _.sumBy(childMaterials, (material) => material.quantity.value ?? 0);

                // add up quantity from original material
                totalQuantity = totalQuantity + (materialModel.quantity.value ?? 0);

                let children: MaterialModel[] = [];

                // If the total quantity is less then or equals the ordered / picked delivered quantity
                let newQuantity = 0;
                if (totalQuantity < materialModel.maxQuantity) {
                    // calculate new quantity for split row by subtracting maxQuantity & total quantity
                    // scenario 1: user decrement the parent row of 6 quantity, and no other child then newQuantity = (6 - 5) = 1
                    // scenario 2: user decrement the parent row of 6 quantity, and one other child then newQuantity = (5 - 4) = 1
                    newQuantity = materialModel.maxQuantity - totalQuantity;
                }
                // check if new quantity is greater than 0, just a false check to make sure minimum quantity 1 is always maintained
                if (newQuantity > 0) {
                    // make a copy of existing model for the split row
                    const newMaterial = materialModel.clone();
                    // assign pickedQuantity as default quantity for new material
                    // set the quantity value to calculated new quantity
                    newMaterial.quantity.value = newQuantity;
                    if (
                        newMaterial.materialStatus.statusCode !== MATERIAL_STATUS_CODE.PICKED &&
                        newMaterial.materialStatus.statusCode !== MATERIAL_STATUS_CODE.NOTRECEIVED
                    ) {
                        // activity date needs to be taken out
                        newMaterial.activityDate.value = moment.invalid();
                    }

                    // check if the split row invoked from pick up scenario
                    // important as row split from "Pickup missing" can be its own parent and can get split further
                    if (splitFromPickup) {
                        // set the max quantity equal to picked quantity
                        newMaterial.maxQuantity = newMaterial.quantity.value;
                        // set the quantity stepper max value to calculated maxQuantity
                        newMaterial.quantity = assignModelOptions(
                            newMaterial.quantity,
                            {
                                ...newMaterial.quantity.options,
                                maxValue: newMaterial.maxQuantity,
                            },
                            newMaterial,
                        );
                        // generate new random id
                        // newMaterial.materialKey = moment.now() + '-' + Math.random() * (1000000 - 10000) + 10000;
                        // additional check added to not show the split material on notification detail screen
                        newMaterial.showOnNotificationDetail = false;

                        // Add new material to the list
                        materials.addModel(newMaterial);

                        // update the maxQuantity of original row to the quantity value
                        materialModel.maxQuantity = materialModel.quantity.value ?? 0;
                    } else {
                        // set the source to split
                        newMaterial.materialSource.sourceCode = MATERIAL_SOURCE_CODE.SPLIT;
                        // set the source row index, so that child have a link to parent
                        newMaterial.parentMaterialModelIndex = index;
                        // generate new random id
                        // newMaterial.materialKey = moment.now() + '-' + Math.random() * (1000000 - 10000) + 10000;
                        // Add new material to the list
                        materials.addModel(newMaterial);

                        // get the child split rows number, like if 1 child row or 2 child rows
                        children = MaterialService.getChildRows(materials, index);

                        // set the max quantity by subtracting delivered quantity to number of child
                        // example: if parent row has maxQuantity = 6 the calculated value for 2nd child is (6-2) = 4
                        newMaterial.maxQuantity = materialModel.maxQuantity - children.length;
                        // set the quantity stepper max value to calculated maxQuantity
                        newMaterial.quantity = assignModelOptions(
                            newMaterial.quantity,
                            {
                                ...newMaterial.quantity.options,
                                maxValue: newMaterial.maxQuantity,
                            },
                            newMaterial,
                        );
                    }
                    // add the new material to all material list
                    this.materials.push(newMaterial);
                    // set the quantity stepper max value to its own quantity value
                    materialModel.quantity = assignModelOptions(
                        materialModel.quantity,
                        {
                            ...materialModel.quantity.options,
                            maxValue: materialModel.quantity.value,
                        },
                        materialModel,
                    );

                    // update maxQuantity for each child rows except the latest created
                    children.forEach((material, indexPosition) => {
                        if (indexPosition !== children.length - 1) {
                            material.maxQuantity =
                                (material.quantity.value ?? 0) +
                                ((materials.value[material.parentMaterialModelIndex].quantity.value ?? 0) -
                                    materials.value[material.parentMaterialModelIndex].quantity.options.minValue);
                            // set the quantity stepper max value to its own max quantity value
                            material.quantity = assignModelOptions(
                                material.quantity,
                                {
                                    ...material.quantity.options,
                                    maxValue: material.maxQuantity,
                                },
                                material,
                            );
                        }
                    });
                    // update the materials in the service
                    this.updateMaterialInMemory(materialModel);
                }
            } else if (
                materialModel.parentMaterialModelIndex > -1 &&
                materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.SPLIT
            ) {
                // 1. if the parent index has a defined value
                // 2. if the split row invoked from a child row
                // get the quantity sum of sibling child for same parent
                const childSumQuantity = _.sumBy(materials.value, (material) => {
                    if (material.parentMaterialModelIndex === materialModel.parentMaterialModelIndex) {
                        return material.quantity.value ?? 0;
                    } else {
                        return 0;
                    }
                });

                // get the new array which has all sibling child object
                const children = materials.value.filter(
                    (material) => material.parentMaterialModelIndex === materialModel.parentMaterialModelIndex,
                );

                const parentMaterial = materials.value[materialModel.parentMaterialModelIndex];

                // On any increment or decrement to child quantity the parent quantity value needs to be update
                // the quantity is calculated subtracting the parent maxQuantity with child sum quantity
                const newValue = materials.value[materialModel.parentMaterialModelIndex].maxQuantity - childSumQuantity;

                // set the quantity stepper max value to its own quantity value
                const parentMaterialNumberOptions = new NumberModelOptions({
                    ...parentMaterial.quantity.options,
                    maxValue: newValue,
                });
                parentMaterial.quantity = <NumberModel>(
                    parentMaterial.set(
                        new NumberModel(parentMaterial.quantity.name, newValue, parentMaterialNumberOptions),
                    )
                );
                // update the material List so that at the end material list can be saved to file storage once
                this.updateMaterialInMemory(parentMaterial);

                // perform for each on the child array
                // it it important as max quantity and quantity stepper max value for each child object has to be changed
                children.forEach((material) => {
                    // calculate the new max quantity by adding subtracted value of parent (quantity - 1) to the its current quantity
                    // example: parent row = 4, 1st child = 1, 2nd child = 1 (delivered quantity is 6)
                    // 1st child maxQuantity = 5, 2nd child maxQuantity = 4
                    // now user made 2nd child = 2 -> parent = 3, 1st child = 1 + (3 - 1) -> 3, 2nd child = 2 + (3 - 1) -> 4
                    // now user made 2nd child = 3 -> parent = 2, 1st child = 1 + (2 - 1) -> 2, 2nd child = 3 + (2 - 1) -> 4
                    material.maxQuantity =
                        (material.quantity.value ?? 0) +
                        ((parentMaterial.quantity.value ?? 0) - parentMaterial.quantity.options.minValue);
                    // set the quantity stepper max value to its own max quantity value
                    material.quantity = assignModelOptions(
                        material.quantity,
                        {
                            ...material.quantity.options,
                            maxValue: material.maxQuantity,
                        },
                        material,
                    );
                    // update the material List so that at the end material list can be saved to file storage once
                    this.updateMaterialInMemory(material);
                });
            }
        }

        return this.saveMaterials(this.materials);
    }

    /**
     * The method is called when the split child row deletes
     * It will merge the deleted row quantity back to parent row
     *
     * @param materials
     * @param index
     */
    public mergeMaterial(materials: ArrayModel<MaterialModel>, index: number): Observable<unknown> {
        if (!this.isInitialized) {
            throw new Error(`MaterialService: mergeMaterial: Trying to call MaterialService before initialization!`);
        }

        // get current material
        const material = materials.value[index];
        const parentMaterial = materials.value[material.parentMaterialModelIndex];

        // if the deleted material is not a child row (having no parent) then do not update anything on parent
        if (material.parentMaterialModelIndex !== -1) {
            // set the parent quantity value by adding existing parent quantity to the deleted row quantity
            const newValue = (parentMaterial.quantity.value ?? 0) + (material.quantity.value ?? 0);

            // set the quantity stepper max value to its own quantity value
            const parentMaterialNumberOptions = new NumberModelOptions({
                ...parentMaterial.quantity.options,
                maxValue: newValue,
            });

            parentMaterial.quantity = <NumberModel>(
                parentMaterial.set(new NumberModel(parentMaterial.quantity.name, newValue, parentMaterialNumberOptions))
            );

            this.updateMaterialInMemory(parentMaterial);

            // update maxQuantity of all child rows
            const childRows = MaterialService.getChildRows(materials, material.parentMaterialModelIndex);

            childRows.forEach((child) => {
                child.maxQuantity =
                    (child.quantity.value ?? 0) +
                    ((parentMaterial.quantity.value ?? 0) - parentMaterial.quantity.options.minValue);
                child.quantity = assignModelOptions(
                    child.quantity,
                    {
                        ...child.quantity.options,
                        maxValue: child.maxQuantity,
                    },
                    child,
                );
                this.updateMaterialInMemory(child);
            });
        }

        // remove the row from list
        materials.removeModel(index);

        // remove the material from the service list
        this.materials = this.materials.filter(
            (m) =>
                !(
                    MaterialIdentifierModel.compare(m.materialIdentifier, material.materialIdentifier) &&
                    m.materialKey === material.materialKey
                ),
        );

        // update the materials in local storage
        return this.saveMaterials(this.materials);
    }

    public saveMaterialCreateOrders(
        notificationMaterialOrder: LocalStorageNotificationMaterialOrderModel,
    ): Observable<unknown> {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: saveMaterialCreateOrders: Trying to call MaterialService before initialization!`,
            );
        }
        const allOrders = this.requestedMaterials;

        const otherRequestedMaterials = allOrders.filter(
            (order) => !notificationMaterialOrder.notificationIdentifier.equalsTo(order.notificationIdentifier),
        );
        otherRequestedMaterials.push(notificationMaterialOrder);
        this.requestedMaterials = otherRequestedMaterials;
        this.requestedMaterialsSubject.next(this.requestedMaterials);
        return this.storageMaterialService.saveMaterialCreateOrders(otherRequestedMaterials, Date.now());
    }

    /**
     * This method returns all the child rows for given parent material row at an index
     *
     * @param materials
     * @param index
     */
    private static getChildRows(materials: ArrayModel<MaterialModel>, index: number): MaterialModel[] {
        return materials.value.filter((material) => material.parentMaterialModelIndex === index);
    }

    /**
     * This method makes a child row independent from its parent row, ie. the child row will now behave as a parent
     *
     * @param childMaterial
     * @param childQuantity
     * @param parentMaterial
     */
    private removeParentRowDependencyFromChild(
        childMaterial: MaterialModel,
        childQuantity: number,
        parentMaterial: MaterialModel,
    ): void {
        childMaterial.parentMaterialModelIndex = -1;
        childMaterial.materialSource.sourceCode = MATERIAL_SOURCE_CODE.ORDERED;
        childMaterial.maxQuantity = childQuantity;
        childMaterial.quantity = assignModelOptions(
            childMaterial.quantity,
            {
                ...childMaterial.quantity.options,
                maxValue: childQuantity,
            },
            childMaterial,
        );
        childMaterial.showOnNotificationDetail = false;
        parentMaterial.maxQuantity = parentMaterial.quantity.value ?? 0;
        parentMaterial.quantity = assignModelOptions(
            parentMaterial.quantity,
            {
                ...parentMaterial.quantity.options,
                maxValue: parentMaterial.quantity.value,
            },
            parentMaterial,
        );
        // update the child material values in local storage
        return this.updateMaterialInMemory(childMaterial);
    }

    /**
     * Return true if material was already requested for the given notification
     *
     * @param notificationIdentifier
     */
    public isMaterialRequested(notificationIdentifier: NotificationIdentifierModel): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: isMaterialRequested: Trying to call MaterialService before initialization!`,
            );
        }
        const allOrders = this.requestedMaterials;
        return allOrders.some(
            (localOrder) =>
                notificationIdentifier.equalsTo(localOrder.notificationIdentifier) && localOrder.isSubmitted,
        );
    }

    /**
     * If materials are requested from PACE then Followup is required
     *
     */
    public isMaterialRequestedForcesFollowup(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: isMaterialRequestedForcesFollowup: Trying to call MaterialService before initialization!`,
            );
        }
        // NGMP58 does not force follow up on material request
        return false;
    }

    /**
     * This function will remove the requested material entry from the local storage
     * based on the matched notification identifier
     *
     * @param notificationIdentifier
     */
    public deleteRequestedMaterialFromLocalStorage(
        notificationIdentifier: NotificationIdentifierModel,
    ): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: deleteRequestedMaterialFromLocalStorage: Trying to call MaterialService before initialization!`,
            );
        }

        const allOrders = this.requestedMaterials;
        const otherRequestedMaterials = allOrders.filter(
            (order) => !notificationIdentifier.equalsTo(order.notificationIdentifier),
        );
        this.requestedMaterials = otherRequestedMaterials;
        this.requestedMaterialsSubject.next(this.requestedMaterials);
        return this.storageMaterialService.saveMaterialCreateOrders(otherRequestedMaterials, Date.now());
    }

    /**
     * Material will only be tracked for a limited amount of time.
     * This function takes care of cleaning the material list from old materials.
     *
     * @param materials
     */
    private removeOldMaterials(materials: MaterialModel[]): MaterialModel[] {
        return this.filterMaterialByMaterialHistoryLimit(materials);
    }

    /**
     * Material Requested will only be tracked for a limited amount of time.
     * This function takes care of cleaning the notification material requested list from old materials.
     *
     * @param localStorageNotificationMaterialOrders
     */
    private removeOldRequestedNotificationMaterials(
        localStorageNotificationMaterialOrders: LocalStorageNotificationMaterialOrderModel[],
    ): LocalStorageNotificationMaterialOrderModel[] {
        return localStorageNotificationMaterialOrders.map((localStorageNotificationMaterialOrder) => {
            localStorageNotificationMaterialOrder.materials = this.filterMaterialByMaterialHistoryLimit(
                localStorageNotificationMaterialOrder.materials,
            );
            return localStorageNotificationMaterialOrder;
        });
    }

    /*
    filter out the material based on material history limit
     */
    private filterMaterialByMaterialHistoryLimit(materials: MaterialModel[]): MaterialModel[] {
        const materialHistoryLimit = this.configService.getMaterialHistoryLimit();
        return materials.filter((material) => {
            if (material.activityDate.value.isValid()) {
                return moment().diff(moment(material.activityDate.value), 'days') <= materialHistoryLimit;
            }
            return true;
        });
    }

    /**
     * This method is to update(overwrite) the materials present in confirmation with localMaterials from storage
     *
     * @param notificationIdentifier
     * @param confirmationMaterialsList
     */
    public updateMaterialListFromLocalStorage(
        notificationIdentifier: NotificationIdentifierModel,
        confirmationMaterialsList: ArrayModel<MaterialModel>,
    ): ArrayModel<MaterialModel> {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: updateMaterialListFromLocalStorage: Trying to call MaterialService before initialization!`,
            );
        }

        const localStorageMaterials = this.getAllMaterialsForNotification(notificationIdentifier);
        if (localStorageMaterials.length > 0) {
            localStorageMaterials.forEach((localStorageMaterialModel: MaterialModel) => {
                const materialIndex = confirmationMaterialsList.value.findIndex((materialModel) =>
                    MaterialIdentifierModel.compare(
                        materialModel.materialIdentifier,
                        localStorageMaterialModel.materialIdentifier,
                    ),
                );
                if (materialIndex > -1) {
                    confirmationMaterialsList.value.splice(materialIndex, 1);
                }
            });
            confirmationMaterialsList.addModels(localStorageMaterials);
        }
        return confirmationMaterialsList;
    }

    /**
     * This method updates the statusCode, statusString and activityDate values of materials
     * which are updated on Confirmation->Material consumption screen
     * to be displayed in material history modal
     *
     * @param materialList
     * @param isConfirmationSubmitted
     * @param materialConsumedDate
     */
    public saveConfirmationMaterialsToLocalStorage(
        materialList: MaterialModel[],
        isConfirmationSubmitted: boolean,
        materialConsumedDate: moment.Moment,
    ): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: saveConfirmationMaterialsToLocalStorage: Trying to call MaterialService before initialization!`,
            );
        }

        this.log.debug(`${this.constructor.name}: saveConfirmationMaterialsToLocalStorage`, {
            numMaterials: materialList?.length,
            isConfirmationSubmitted,
            materialConsumedDate: materialConsumedDate.toISOString(),
        });

        materialList.forEach((materialModel) => {
            if (
                (materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED ||
                    materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK ||
                    materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.OTHERSTOCK ||
                    materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.SPLIT ||
                    materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.OTHER) &&
                materialModel.activityDate.value.isValid() &&
                materialModel.consumptionCode.selectedEntry
            ) {
                // if the confirmation is not submitted
                if (!isConfirmationSubmitted) {
                    materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.PICKED;
                    materialModel.statusString.value = 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_PICKEDUP';

                    // override picked status to "Not Received"
                    if (materialModel.consumptionCode.selectedEntry.value === CONSUME_CODE.NOT_RECEIVED) {
                        materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.NOTRECEIVED;
                        materialModel.statusString.value = 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_NOT_RECEIVED';
                    }
                } else {
                    // if the confirmation is submitted
                    // User already clicked on declare button once
                    if (materialModel.isConsumed) {
                        switch (materialModel.consumptionCode.selectedEntry.value) {
                            case CONSUME_CODE.CONSUMED:
                                materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.CONSUMED;
                                materialModel.statusString.value = this.clientManager.isNgmP58()
                                    ? 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_CONSUMED'
                                    : 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_LOCAL_SCRAP';
                                break;
                            case CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION:
                                materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.RETURNWITHOUTCONSUME;
                                materialModel.statusString.value =
                                    'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_RETURNWITHOUTCONSUME';

                                break;
                            case CONSUME_CODE.LEFT_ON_NOTIFICATION:
                                materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.LEFTONNOTIFICATION;
                                materialModel.statusString.value =
                                    'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_LEFTONNOTIFICATION';

                                break;
                            case CONSUME_CODE.NOT_RECEIVED:
                                materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.NOTRECEIVED;
                                materialModel.statusString.value =
                                    'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_NOT_RECEIVED';

                                break;
                        }
                        // override status previously set if the return exists
                        // if the return type is visible and the current material status is not returned when the consumption code is "Consumed" or "Return without consumption"
                        // When consumption code is "LEFTONNOTIFICATION"  it is not mandatory to define return type.
                        if (
                            materialModel.returnType.selectedEntry &&
                            (materialModel.returnType.selectedEntry.value === RETURN_CODE.CENTRAL_RETURN ||
                                materialModel.returnType.selectedEntry.value === RETURN_CODE.CENTRAL_SCRAP)
                        ) {
                            materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.TOBERETURNED;
                            materialModel.statusString.value =
                                'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_TOBERETURNED';
                        } else if (
                            materialModel.returnType.selectedEntry &&
                            materialModel.returnType.selectedEntry.value === RETURN_CODE.LOCAL_SCRAP
                        ) {
                            // If the return type is "Local Scrap" , the material cannot be returned.
                            // So, the material status will set to consumed.
                            materialModel.materialStatus.statusCode = MATERIAL_STATUS_CODE.CONSUMED;
                            materialModel.statusString.value = this.clientManager.isNgmP58()
                                ? 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_CONSUMED'
                                : 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_LOCAL_SCRAP';
                        } else {
                            // “A material is left on a notification cannot be returned, so not having a return type is expected and not an error”
                            if (materialModel.materialStatus.statusCode !== MATERIAL_STATUS_CODE.LEFTONNOTIFICATION) {
                                this.log.error(
                                    `MaterialService: No suitable returnType found for the material ${materialModel.materialIdentifier.toString()} in status ${
                                        materialModel.materialStatus.statusCode
                                    } when the confirmation is submitted`,
                                );
                            }
                        }
                    }
                }
                materialModel.activityDate.value = materialConsumedDate;

                // save the material updates to local storage
                this.updateMaterialInMemory(materialModel);
            } else {
                this.log.debug(
                    `MaterialService: No suitable status found for material ${materialModel.materialIdentifier.toString()}, with source ${
                        materialModel.materialSource.sourceCode
                    }`,
                );
            }
        });

        return this.saveMaterials(this.materials);
    }

    public getSavedMaterialOrderModel(
        notificationIdentifier: NotificationIdentifierModel,
    ): Observable<LocalStorageNotificationMaterialOrderModel | undefined> {
        if (!this.isInitialized) {
            throw new Error(
                `MaterialService: getSavedMaterialOrderModel: Trying to call MaterialService before initialization!`,
            );
        }
        return this.updateLocalRequestedMaterials().pipe(
            map(() =>
                this.requestedMaterials.find((localStorageMaterialOrderModel) =>
                    notificationIdentifier.equalsTo(localStorageMaterialOrderModel.notificationIdentifier),
                ),
            ),
        );
    }

    /**
     * If the material's notification is no longer on device, and the material has valid AWB return number saved,
     * and the status is still "Picked", then it should be assumed that confirmation was sent and
     * that is the reason that notification is no longer on device, and these materials status are not updated correctly
     */
    private getUnprocessedMaterials(): MaterialModel[] {
        return this.materials.filter((material) => {
            const notification = this.notificationService.getNotificationByIdentifier(
                new NotificationIdentifierModelFromJson(material.notificationIdentifier),
            );
            if (
                typeof notification === 'undefined' &&
                material.awbReturn.value.length > 0 &&
                material.materialStatus.statusCode === MATERIAL_STATUS_CODE.PICKED
            ) {
                this.log.warn(`Unprocessed Material identified ${material.materialIdentifier.materialNumber.value}`);
                return material;
            }
        });
    }

    /**
     * This is a workaround for the issues when the confirmation is submitted and app is closed too soon,
     * such that materials to be returned are not saved with correct status.
     * By calling this method, we first identify such materials, and then their status is updated correctly
     * assuming materialConsumedDate is now i.e. moment(), so that they are displayed in material return screen.
     */
    public updateUnprocessedMaterials(): Observable<void> {
        if (this.configService.getEnableMatReturnWorkAround()) {
            const unprocessedMaterials = this.getUnprocessedMaterials();
            if (unprocessedMaterials.length > 0) {
                return this.saveConfirmationMaterialsToLocalStorage(unprocessedMaterials, true, moment()).pipe(
                    tap(() => this.eventService.triggerRefreshMaterialReturnCount()),
                );
            }
        }
        return of(undefined);
    }

    private saveMaterials(materials: MaterialModel[]): Observable<void> {
        const timestamp = Date.now();
        return this.storageMaterialService.saveMaterials(materials, timestamp);
    }
}
